import React, { useState } from 'react';
import { toast } from 'sonner';
import { updateClinicianPatient } from '../lib/api';

const STAGES = [
  { value: 'pre', label: 'Pre-menopause' },
  { value: 'peri', label: 'Perimenopause' },
  { value: 'post', label: 'Post-menopause' },
];
const inputCls = 'w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white placeholder-white/30 outline-none focus:border-[var(--aura-purple-light)]';
const labelCls = 'block text-xs text-white/50 mb-1';

export function EditPatientModal({ clinicianId, patient, onClose, onSaved }) {
  const placeholder = (patient.email || '').endsWith('@patients.auralis.local');
  const [form, setForm] = useState({
    name: patient.name || '',
    date_of_birth: patient.date_of_birth || '',
    menopause_stage: ['pre', 'peri', 'post'].includes(patient.stage) ? patient.stage : 'peri',
    email: placeholder ? '' : patient.email || '',
    phone: patient.phone || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      toast.error('Please enter the patient\u2019s name');
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email address or leave it blank');
      return;
    }
    setSaving(true);
    try {
      const res = await updateClinicianPatient(clinicianId, patient.id, {
        name: form.name.trim(),
        date_of_birth: form.date_of_birth,
        menopause_stage: form.menopause_stage,
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      toast.success('Patient details updated');
      onSaved?.(res.patient);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update patient. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/60 backdrop-blur-sm" data-testid="edit-patient-modal" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass-dark w-full max-w-lg rounded-3xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-display text-2xl text-white">Edit patient details</h2>
            <p className="text-white/50 text-sm">Changes are recorded in the audit log.</p>
          </div>
          <button type="button" data-testid="edit-patient-close" onClick={onClose} className="text-white/50 hover:text-white w-8 h-8 grid place-items-center rounded-full hover:bg-white/10"><i className="fa-solid fa-xmark" /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <div className="sm:col-span-2">
            <label className={labelCls}>Full name *</label>
            <input data-testid="edit-patient-name" value={form.name} onChange={set('name')} className={inputCls} autoFocus />
          </div>
          <div>
            <label className={labelCls}>Date of birth</label>
            <input data-testid="edit-patient-dob" type="date" value={form.date_of_birth} onChange={set('date_of_birth')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Stage</label>
            <select data-testid="edit-patient-stage" value={form.menopause_stage} onChange={set('menopause_stage')} className={inputCls}>
              {STAGES.map((s) => <option key={s.value} value={s.value} className="text-black">{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Email <span className="text-white/30">(optional)</span></label>
            <input data-testid="edit-patient-email" type="email" value={form.email} onChange={set('email')} placeholder="patient@example.com" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phone <span className="text-white/30">(optional)</span></label>
            <input data-testid="edit-patient-phone" value={form.phone} onChange={set('phone')} placeholder="07…" className={inputCls} />
          </div>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-2xl py-3 border border-white/15 text-white/80 hover:bg-white/5 transition-base">Cancel</button>
          <button type="submit" data-testid="edit-patient-submit" disabled={saving} className="flex-1 aura-btn-gradient text-white font-semibold rounded-2xl py-3 flex items-center justify-center gap-2 disabled:opacity-70">
            {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-check" /> Save changes</>}
          </button>
        </div>
      </form>
    </div>
  );
}
